<template>
  <transition name="modal-fade">
    <div v-if="visible" class="xlsx-modal-overlay" @click="handleCancel">
      <div class="xlsx-modal" @click.stop>
        <!-- 關閉按鈕 -->
        <div class="xlsx-close-btn" @click="handleCancel">
          <img src="@/statics/iconsvg/interaction/Close.svg" class="q-icon" />
        </div>

        <!-- 標題列 -->
        <div class="xlsx-modal-header">
          <span class="xlsx-modal-title">{{ $t('board.xlsxImport.標題') }}</span>
        </div>

        <!-- 內容區 -->
        <div class="xlsx-modal-body">
          <!-- 檔案名稱 -->
          <div class="file-info">
            <span class="file-label">{{ $t('board.xlsxImport.檔案') }}</span>
            <span class="file-name">{{ fileName }}</span>
            <div
              v-if="fileName"
              class="file-close-btn"
              @click="handleClearFile"
            >
              <img src="@/statics/iconsvg/interaction/Close.svg" class="q-icon" />
            </div>
          </div>

          <!-- 重新選擇文件區域 -->
          <div v-if="!fileName" class="reselect-file-area">
            <input
              ref="fileInput"
              type="file"
              accept=".xlsx,.xls"
              @change="handleFileChange"
              style="display: none;"
            />
            <Button
              type="primary"
              size="large"
              icon="ios-cloud-upload-outline"
              @click="triggerFileInput"
            >
              {{ $t('board.xlsxImport.選擇文件') }}
            </Button>
            <p class="file-hint">{{ $t('board.xlsxImport.格式提示') }}</p>
          </div>

          <!-- 題目預覽表格 -->
          <div v-if="fileName" class="preview-table">
            <Table
              :columns="tableColumns"
              :data="questions"
              :height="400"
              size="small"
              border
              stripe
            />
          </div>
        </div>

        <!-- 底部提示區 -->
        <div class="xlsx-modal-hint">
          <Icon type="ios-information-circle-outline" size="16" />
          <span>{{ $t('board.xlsxImport.格式問題') }}</span>
          <a href="/template.xlsx" download="template.xlsx" class="template-link">
            {{ $t('board.xlsxImport.下載範例') }}
          </a>
        </div>

        <!-- 底部按鈕 -->
        <div class="xlsx-modal-footer">
          <Button @click="handleCancel" size="large" class="cancel-btn">
            {{ $t('board.xlsxImport.取消') }}
          </Button>
          <Button
            type="success"
            @click="handleImportToBoard"
            size="large"
            :loading="importing"
            :disabled="questions.length === 0"
            class="import-btn"
          >
            <Icon type="ios-cloud-upload" v-if="!importing" />
            {{ importing ? `${$t('board.xlsxImport.導入中')} ${importProgress}%` : $t('board.xlsxImport.導入到畫板') }}
          </Button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import { XlsxQuizParser } from '@/plugin/XlsxQuizParser';
import { QuizImageConverter } from '@/plugin/QuizImageConverter';
import LatexRenderer from './LatexRenderer.vue';

export default {
  name: 'XlsxQuizUploader',
  components: {
    LatexRenderer
  },
  data() {
    return {
      visible: false,
      fileName: '',
      questions: [],
      parser: null,
      importing: false,
      importProgress: 0,
      currentConverter: null, // 當前的 converter 實例
      tableColumns: [
        {
          title: this.$t('board.xlsxImport.欄位題號'),
          key: 'itemIndex',
          width: 60,
          align: 'center'
        },
        {
          title: this.$t('board.xlsxImport.欄位分數'),
          key: 'point',
          width: 80,
          align: 'center'
        },
        {
          title: this.$t('board.xlsxImport.欄位答案'),
          key: 'answer',
          width: 150,
          align: 'center',
          render: (h, params) => {
            const answer = params.row.answer;
            if (!answer || answer === '*') {
              return h('span', { style: { color: '#999' } }, '*');
            }
            // 如果答案包含數學公式，使用 LatexRenderer
            if (answer.includes('$') || answer.includes('\\')) {
              return h(LatexRenderer, {
                props: { content: answer }
              });
            }
            return h('span', answer);
          }
        },
        {
          title: this.$t('board.xlsxImport.欄位題型'),
          key: 'type',
          width: 100,
          align: 'center',
          render: (h, params) => {
            const type = params.row.type;
            let displayText = '';
            let color = '#333';

            if (type === 'single' || type === 'judge') {
              displayText = '單選題';
              color = '#19be6b';
            } else if (type === 'complete' || type === 'subjective') {
              displayText = '文字題';
              color = '#2d8cf0';
            } else {
              displayText = '不支援';
              color = '#ed4014';
            }

            return h('span', { style: { color: color, fontWeight: '500' } }, displayText);
          }
        },
        {
          title: this.$t('board.xlsxImport.欄位題幹'),
          key: 'question',
          minWidth: 250,
          render: (h, params) => {
            const question = params.row.question || '';
            return h(LatexRenderer, {
              props: {
                content: question
              },
              style: {
                display: 'block',
                padding: '4px 0'
              }
            });
          }
        },
        {
          title: this.$t('board.xlsxImport.欄位選項1'),
          key: 'option1',
          width: 150,
          render: (h, params) => {
            const option = params.row.options && params.row.options[0];
            if (!option) return h('span', '-');
            return h(LatexRenderer, {
              props: { content: option }
            });
          }
        },
        {
          title: this.$t('board.xlsxImport.欄位選項2'),
          key: 'option2',
          width: 150,
          render: (h, params) => {
            const option = params.row.options && params.row.options[1];
            if (!option) return h('span', '-');
            return h(LatexRenderer, {
              props: { content: option }
            });
          }
        },
        {
          title: this.$t('board.xlsxImport.欄位選項3'),
          key: 'option3',
          width: 150,
          render: (h, params) => {
            const option = params.row.options && params.row.options[2];
            if (!option) return h('span', '-');
            return h(LatexRenderer, {
              props: { content: option }
            });
          }
        },
        {
          title: this.$t('board.xlsxImport.欄位選項4'),
          key: 'option4',
          width: 150,
          render: (h, params) => {
            const option = params.row.options && params.row.options[3];
            if (!option) return h('span', '-');
            return h(LatexRenderer, {
              props: { content: option }
            });
          }
        }
      ]
    };
  },
  methods: {
    /**
     * 開啟對話框並解析文件
     */
    async open(file) {
      this.visible = true;

      if (!file) {
        // 如果沒有文件，顯示空白狀態，等待用戶選擇
        this.fileName = '';
        this.questions = [];
        return;
      }

      this.fileName = file.name;
      this.questions = [];

      try {
        this.parser = new XlsxQuizParser();
        const result = await this.parser.parseFile(file);

        this.questions = result.questions;

        if (result.errors && result.errors.length > 0) {
          console.warn('解析警告：', result.errors);
          this.$Message.warning(this.$t('board.xlsxImport.成功解析有警告', { count: this.questions.length, errors: result.errors.length }));
        } else {
          this.$Message.success(this.$t('board.xlsxImport.成功解析', { count: this.questions.length }));
        }
      } catch (error) {
        console.error('解析失敗：', error);
        this.$Message.error(this.$t('board.xlsxImport.解析失敗', { error: error.message }));
        this.visible = false;
      }
    },

    /**
     * 觸發文件選擇
     */
    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    /**
     * 處理文件選擇
     */
    async handleFileChange(e) {
      const file = e.target.files[0];
      if (!file) return;

      // 重置文件輸入
      this.$refs.fileInput.value = '';

      this.fileName = file.name;
      this.questions = [];

      try {
        this.parser = new XlsxQuizParser();
        const result = await this.parser.parseFile(file);

        this.questions = result.questions;

        if (result.errors && result.errors.length > 0) {
          console.warn('解析警告：', result.errors);
          this.$Message.warning(this.$t('board.xlsxImport.成功解析有警告', { count: this.questions.length, errors: result.errors.length }));
        } else {
          this.$Message.success(this.$t('board.xlsxImport.成功解析', { count: this.questions.length }));
        }
      } catch (error) {
        console.error('解析失敗：', error);
        this.$Message.error(this.$t('board.xlsxImport.解析失敗', { error: error.message }));
        this.fileName = '';
        this.questions = [];
      }
    },

    /**
     * 清除文件
     */
    handleClearFile() {
      this.fileName = '';
      this.questions = [];
    },

    /**
     * 取消
     */
    handleCancel() {
      // 如果正在匯入，取消當前的轉換操作
      if (this.importing && this.currentConverter) {
        this.currentConverter.cancel();
        console.log('🛑 已取消匯入操作');
      }

      this.visible = false;
      this.fileName = '';
      this.questions = [];
      this.importing = false;
      this.importProgress = 0;
      this.currentConverter = null;
      this.$emit('cancel');
    },

    /**
     * 導入到畫板
     */
    async handleImportToBoard() {
      if (this.questions.length === 0) {
        this.$Message.warning(this.$t('board.xlsxImport.沒有可匯入的題目'));
        return;
      }

      // 顯示轉換提示訊息（可關閉，不自動消失）
      const loadingMsg = this.$Message.info({
        content: this.$t('board.xlsxImport.轉換提示'),
        duration: 0,
        closable: true
      });

      this.importing = true;
      this.importProgress = 0;

      try {
        // 1. 轉換為圖片
        const converter = new QuizImageConverter();
        this.currentConverter = converter; // 保存實例引用
        const images = await converter.convertBatch(this.questions, (progress) => {
          this.importProgress = Math.round(progress.percentage / 2); // 前 50%
        });

        // 轉換完成後關閉載入提示
        if (loadingMsg && loadingMsg.close) {
          loadingMsg.close();
        }

        // 檢查是否已取消
        if (converter.cancelled) {
          this.$Message.info(this.$t('board.xlsxImport.匯入已取消'));
          converter.destroy();
          this.currentConverter = null;
          return;
        }

        // 檢查是否有失敗的
        const failedCount = images.filter(img => img === null).length;
        if (failedCount > 0) {
          this.$Message.warning(this.$t('board.xlsxImport.轉換完成', { success: images.length - failedCount, failed: failedCount }));
        }

        // 過濾掉失敗的題目
        const validImages = [];
        const validQuestions = [];
        images.forEach((img, i) => {
          if (img !== null) {
            validImages.push(img);
            validQuestions.push(this.questions[i]);
          }
        });

        if (validImages.length === 0) {
          this.$Message.error(this.$t('board.xlsxImport.所有題目轉換失敗'));
          converter.destroy();
          this.currentConverter = null;
          return;
        }

        // 2. 清理資源
        converter.destroy();
        this.currentConverter = null;

        // 3. 發送給父組件（Board.vue）處理渲染和配置
        this.$emit('import', {
          questions: validQuestions,
          images: validImages
        });

        this.importProgress = 100;

        // 成功提示
        this.$Message.success(this.$t('board.xlsxImport.成功導入', { count: validImages.length }));

        // 關閉對話框
        setTimeout(() => {
          this.visible = false;
          this.fileName = '';
          this.questions = [];
          this.importing = false;
          this.importProgress = 0;
        }, 500);

      } catch (error) {
        console.error('導入失敗:', error);
        // 發生錯誤時也關閉載入提示
        if (loadingMsg && loadingMsg.close) {
          loadingMsg.close();
        }
        this.$Message.error(this.$t('board.xlsxImport.導入失敗', { error: error.message }));
      } finally {
        this.importing = false;
      }
    },

    /**
     * 確認（保留舊方法，以防需要）
     */
    handleConfirm() {
      if (this.questions.length === 0) {
        this.$Message.warning(this.$t('board.xlsxImport.沒有可匯入的題目'));
        return;
      }

      this.$emit('confirm', {
        questions: this.questions,
        fileName: this.fileName
      });

      this.visible = false;
      this.fileName = '';
      this.questions = [];
    }
  }
};
</script>

<style lang="less" scoped>
// Modal 覆蓋層
.xlsx-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 900;
}

// Modal 主體
.xlsx-modal {
  position: relative;
  background: #ececec;
  border-radius: 20px;
  width: 90vw;
  max-width: 1200px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
}

// 關閉按鈕（圓形）
.xlsx-close-btn {
  cursor: pointer;
  position: absolute;
  top: -12px;
  right: -12px;
  width: 30px;
  height: 30px;
  line-height: 27px;
  background-color: #d8d8d8;
  text-align: center;
  border-radius: 50%;
  z-index: 100;

  img {
    width: 80%;
  }
}

// 標題列
.xlsx-modal-header {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 24px 16px;
}

.xlsx-modal-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

// 內容區
.xlsx-modal-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 16px 32px 24px;
}

// 檔案信息
.file-info {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding: 14px 50px 14px 16px;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.file-label {
  font-weight: 600;
  color: #666;
  margin-right: 8px;
}

.file-name {
  color: #2d8cf0;
  font-weight: 500;
  flex: 1;
}

// 檔案關閉按鈕
.file-close-btn {
  cursor: pointer;
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.7;
  }

  img {
    width: 100%;
    height: 100%;
    filter: brightness(0) saturate(100%) invert(24%) sepia(89%) saturate(6943%) hue-rotate(352deg) brightness(93%) contrast(107%);
  }
}

// 重新選擇文件區域
.reselect-file-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  border: 2px dashed #c0c0c0;
  border-radius: 12px;
  background: #f8f8f8;
  min-height: 400px;
}

.file-hint {
  margin-top: 16px;
  color: #999;
  font-size: 14px;
}

// 預覽表格
.preview-table {
  flex: 1;
  overflow: hidden;
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

// 底部提示區
.xlsx-modal-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 32px 0;
  font-size: 14px;
  color: #666;

  .template-link {
    color: #2d8cf0;
    text-decoration: none;
    font-weight: 500;
    transition: all 0.2s;

    &:hover {
      color: #57a3f3;
      text-decoration: underline;
    }
  }
}

// 底部按鈕
.xlsx-modal-footer {
  padding: 20px 32px 24px;
  text-align: right;
  background: transparent;
}

.cancel-btn {
  margin-right: 12px;
  min-width: 100px;
}

.import-btn {
  min-width: 140px;
}

.confirm-btn {
  min-width: 100px;
}

// Modal 過渡動畫
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-active .xlsx-modal,
.modal-fade-leave-active .xlsx-modal {
  transition: all 0.3s ease;
}

.modal-fade-enter .xlsx-modal,
.modal-fade-leave-to .xlsx-modal {
  transform: scale(0.95);
  opacity: 0;
}
</style>
